<template>
  <div
    class="flex flex-col bg-primary border border-secondary rounded-md overflow-hidden"
  >
    <TaskLinkedTo
      :task="task"
      :readonly="!canEdit"
      @update="$emit('update', $event)"
    />
    <div
      class="flex flex-col gap-3 p-4 text-style-sm font-medium lg:text-secondary"
    >
      <div class="flex flex-row gap-2 items-center justify-start">
        <Icon name="branch" class="fg-quaternary" />
        <span class="flex-1 text-left rtl:text-right">
          {{ $t("components.task_details.properties.status.label") }}
        </span>
        <Status :status="task.status" :size="maxTablet ? 'lg' : 'md'" />
      </div>

      <Component
        :is="!readonly ? DateSelectModal : 'div'"
        :variant="maxTablet ? undefined : 'property'"
        size="fit"
        :title="$t('components.task_details.due_date.title')"
        :description="$t('components.task_details.due_date.description')"
        :initialState="task.scheduled_for"
        :minDate="new Date()"
        @update="updateDueDate"
      >
        <div class="flex flex-row gap-2 items-center justify-start">
          <Icon name="calendar-time" class="fg-quaternary" />
          <span class="flex-1 text-left rtl:text-right">
            {{ $t("components.task_details.properties.due_date.label") }}
          </span>
          <DueDate
            :dueDate="task.scheduled_for"
            :size="maxTablet ? 'lg' : 'md'"
            :isOverdue="isOverdue(task.completed_at, task.scheduled_for)"
            :placeholder="
              $t('components.task_details.properties.due_date.placeholder')
            "
          />
        </div>
      </Component>

      <div
        v-if="task.completed_at"
        class="flex flex-row gap-2 items-center justify-start"
      >
        <Icon name="calendar-check" class="fg-quaternary" />
        <span class="flex-1 text-left rtl:text-right">
          {{ $t("components.sorting.completed_at.label") }}
        </span>

        <span class="text-style-base lg:text-style-sm text-secondary">
          {{ formatDate(task.completed_at) }}
        </span>
      </div>

      <Component
        :is="canEdit ? PrioritySelectModal : 'div'"
        :variant="maxTablet ? undefined : 'property'"
        size="fit"
        :title="$t('components.task_details.priority.title')"
        :description="$t('components.task_details.priority.description')"
        :initialState="task.priority?.id"
        @update="updatePriority"
      >
        <div class="flex flex-row gap-2 items-center justify-start">
          <Icon name="error-circle" class="fg-quaternary" />
          <span class="flex-1 text-left rtl:text-right">
            {{ $t("components.task_details.properties.priority.label") }}
          </span>
          <Priority
            :priority="task.priority?.id"
            variant="light"
            :size="maxTablet ? 'lg' : 'md'"
            :hideIcon="!maxTablet"
          />
        </div>
      </Component>

      <Component
        :is="canEdit ? CategorySelectModal : 'div'"
        :title="$t('components.task_details.category.title')"
        :description="$t('components.task_details.category.description')"
        :initialState="task.category"
        :buildingIds="[task.tenant.id]"
        @update="updateCategory"
      >
        <div class="flex flex-row gap-2 items-center justify-start">
          <Icon name="hashtag-square" class="fg-quaternary" />
          <span class="flex-1 text-left rtl:text-right">
            {{ $t("components.task_details.properties.category.label") }}
          </span>
          <Badge
            v-if="task.category?.name"
            :label="task.category?.name"
            type="light"
            color="indigo"
            :size="maxTablet ? 'lg' : 'md'"
            class="max-w-[180px] truncate"
          />
          <span
            v-else
            class="text-style-base lg:text-style-sm text-placeholder_subtle"
          >
            {{
              $t(
                `components.task_details.properties.category.${
                  canEdit ? "cta" : "empty"
                }`
              )
            }}
          </span>
        </div>
      </Component>

      <Component
        :is="canEdit ? UserSelectModal : 'div'"
        :key="task.assignees?.length"
        :variant="maxTablet ? undefined : 'property'"
        size="fit"
        :title="$t('components.task_details.assignees.title')"
        :description="$t('components.task_details.assignees.description')"
        :initialState="task.assignees"
        :buildingIds="[task.tenant.id]"
        @update="updateAssignees"
      >
        <div class="flex flex-row gap-2 items-center justify-start">
          <Icon name="users-default" class="fg-quaternary" />
          <span class="flex-1 text-left rtl:text-right">
            {{ $t("components.task_details.properties.assignees.label") }}
          </span>
          <Assignees
            :assignees="task.assignees"
            :size="maxTablet ? 'md' : 'sm'"
            :showNames="!maxTablet"
          />
        </div>
      </Component>
    </div>
    <TaskLoggedTimeAndMaterial
      :task="task"
      :canLogTime="canLogTime"
      :canLogMaterial="canLogMaterial"
      @logTime="$emit('logTime')"
      @showTimeLog="$emit('showTimeLog')"
      @logMaterial="$emit('logMaterial')"
      @showMaterialLog="$emit('showMaterialLog')"
    />
  </div>
</template>

<script setup>
import { Assignees } from "@/SharedComponents/Assignees";
import { Badge, Priority, Status } from "@/SharedComponents/Badges";
import { DueDate } from "@/SharedComponents/Dates";
import { Icon } from "@/SharedComponents/Icon";
import {
  CategorySelectModal,
  DateSelectModal,
  PrioritySelectModal,
  UserSelectModal,
} from "@/SharedComponents/Modals";
import {
  TaskLinkedTo,
  TaskLoggedTimeAndMaterial,
} from "@/SharedComponents/Tasks/TaskDetailsParts";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { includeHours } from "@/Utils/time";
import { toUTC } from "@/Utils/date";
import { useDate } from "@/Composables/useDate";
import { endOfDay } from "date-fns";

const { maxTablet } = useBreakpoints();
const { formatDate } = useDate();

const emit = defineEmits([
  "update",
  "logTime",
  "showTimeLog",
  "logMaterial",
  "showMaterialLog",
]);

defineProps({
  task: { type: Object, required: true },
  canEdit: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  canLogTime: { type: Boolean, default: false },
  canLogMaterial: { type: Boolean, default: false },
});

function updateDueDate(value) {
  emit("update", {
    schedule_for: value ? toUTC(includeHours(value).toISOString()) : null,
  });
}

function updatePriority(value) {
  emit("update", { priority: value });
}

function updateAssignees(value) {
  emit("update", { assignee_id: value.map((user) => user.id) });
}

function updateCategory(value) {
  emit("update", { category_id: value?.id ?? null });
}

const isOverdue = (completed_at, scheduled_for) => {
  if (completed_at) {
    return endOfDay(new Date(completed_at)) > endOfDay(new Date(scheduled_for));
  }

  return (
    scheduled_for && endOfDay(new Date()) > endOfDay(new Date(scheduled_for))
  );
};
</script>
